/*
 * BN Native App — global behaviours
 * ─────────────────────────────────
 *   1) Bottom-nav cart badge — mirrors the header cart count
 *   2) Hide-on-scroll header  — header tucks away when scrolling down
 *   3) Search overlay         — full-screen predictive search
 *
 * Vanilla, dependency-free. Interaction is delegated on `document` so it
 * survives section re-renders. Loaded site-wide with `defer`.
 */
(function () {
  'use strict';

  /* ── 1) Bottom-nav cart badge ──────────────────────────────────── */
  (function cartBadge() {
    var badge = document.querySelector('[data-bn-nav-cart-count]');
    if (!badge) return;
    var src = document.querySelector('[data-header-cart-count]');

    function paint(n) {
      n = parseInt(n, 10) || 0;
      badge.textContent = n;
      if (n > 0) badge.removeAttribute('hidden');
      else badge.setAttribute('hidden', '');
    }

    // The cart drawer keeps [data-header-cart-count] current for every cart
    // mutation (drawer edits, flyout adds) — mirror it onto the nav badge.
    if (src) {
      paint(src.getAttribute('data-header-cart-count'));
      new MutationObserver(function () {
        paint(src.getAttribute('data-header-cart-count'));
      }).observe(src, { attributes: true, attributeFilter: ['data-header-cart-count'] });
    }
    document.addEventListener('cart:updated', function (e) {
      if (e.detail && typeof e.detail.item_count === 'number') paint(e.detail.item_count);
    });
  })();

  /* ── 2) Hide-on-scroll header ──────────────────────────────────── */
  (function hideOnScroll() {
    var root = document.documentElement;
    var last = window.pageYOffset || 0;
    var ticking = false;
    var THRESHOLD = 6;   // ignore sub-pixel / micro scrolls
    var TOP_ZONE = 80;   // always reveal the header near the top

    function update() {
      ticking = false;
      var y = window.pageYOffset || 0;
      if (y <= TOP_ZONE) { root.removeAttribute('data-bn-scroll'); last = y; return; }
      var dy = y - last;
      if (Math.abs(dy) < THRESHOLD) return;
      root.setAttribute('data-bn-scroll', dy > 0 ? 'down' : 'up');
      last = y;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
  })();

  /* ── 3) Search overlay ─────────────────────────────────────────── */
  (function searchOverlay() {
    var overlay = document.querySelector('[data-bn-search]');
    if (!overlay) return;
    var input = overlay.querySelector('[data-bn-search-input]');
    var panel = overlay.querySelector('[data-bn-search-results]');
    var clearBtn = overlay.querySelector('[data-bn-search-clear]');
    var form = overlay.querySelector('[data-bn-search-form]');
    var searchUrl = (form && form.getAttribute('action')) || '/search';
    var lastFocus = null;
    var timer = null;
    var reqId = 0;

    function isOpen() { return !overlay.hidden; }

    function open() {
      if (isOpen()) return;
      lastFocus = document.activeElement;
      overlay.hidden = false;
      document.body.classList.add('bn-search-open');
      hint();
      setTimeout(function () { if (input) input.focus(); }, 30);
    }
    function close() {
      overlay.hidden = true;
      document.body.classList.remove('bn-search-open');
      if (input) input.value = '';
      if (clearBtn) clearBtn.hidden = true;
      panel.innerHTML = '';
      if (lastFocus && lastFocus.focus) { try { lastFocus.focus(); } catch (e) {} }
      lastFocus = null;
    }

    function el(tag, cls, text) {
      var n = document.createElement(tag);
      if (cls) n.className = cls;
      if (text != null) n.textContent = text;
      return n;
    }
    function message(cls, text) {
      panel.innerHTML = '';
      panel.appendChild(el('p', cls, text));
    }
    function hint() { message('bn-search__hint', 'Search for products, brands and more.'); }

    function render(products, q) {
      panel.innerHTML = '';
      if (!products.length) {
        message('bn-search__empty', 'No matches for “' + q + '”.');
        return;
      }
      products.forEach(function (p) {
        var a = el('a', 'bn-search__result');
        a.href = p.url;
        var img = el('img', 'bn-search__result-img');
        img.loading = 'lazy';
        img.alt = '';
        img.src = p.image || (p.featured_image && p.featured_image.url) || '';
        if (!img.src) img.style.visibility = 'hidden';
        var body = el('div', 'bn-search__result-body');
        body.appendChild(el('div', 'bn-search__result-title', p.title));
        if (p.vendor) body.appendChild(el('div', 'bn-search__result-meta', p.vendor));
        a.appendChild(img);
        a.appendChild(body);
        panel.appendChild(a);
      });
      var all = el('a', 'bn-search__all', 'See all results for “' + q + '”');
      all.href = searchUrl + '?q=' + encodeURIComponent(q) + '&type=product';
      panel.appendChild(all);
    }

    function search(q) {
      var mine = ++reqId;
      fetch('/search/suggest.json?q=' + encodeURIComponent(q) +
            '&resources[type]=product&resources[limit]=8' +
            '&resources[options][unavailable_products]=last',
            { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (mine !== reqId || !isOpen()) return; // ignore stale responses
          var products = (data && data.resources && data.resources.results &&
                          data.resources.results.products) || [];
          render(products, q);
        })
        .catch(function () {
          if (mine === reqId) message('bn-search__empty', 'Search is unavailable right now.');
        });
    }

    function skeleton() {
      panel.innerHTML = '';
      for (var i = 0; i < 6; i++) {
        var row = el('div', 'bn-search__skel');
        row.appendChild(el('div', 'bn-skel bn-skel-img'));
        var lines = el('div', 'bn-skel-lines');
        lines.appendChild(el('div', 'bn-skel bn-skel-line'));
        lines.appendChild(el('div', 'bn-skel bn-skel-line is-short'));
        row.appendChild(lines);
        panel.appendChild(row);
      }
    }

    function onInput() {
      var q = input.value.trim();
      if (clearBtn) clearBtn.hidden = !q;
      clearTimeout(timer);
      if (q.length < 2) { hint(); return; }
      skeleton();
      timer = setTimeout(function () { search(q); }, 220);
    }

    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('[data-bn-search-open]')) { e.preventDefault(); open(); return; }
      if (e.target.closest('[data-bn-search-close]')) { e.preventDefault(); close(); return; }
      if (e.target.closest('[data-bn-search-clear]')) {
        input.value = '';
        clearBtn.hidden = true;
        hint();
        input.focus();
      }
    });
    if (input) input.addEventListener('input', onInput);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && isOpen()) close();
    });
  })();

  /* ── 4) Haptic feedback (Android; iOS Safari has no vibrate) ───── */
  (function haptics() {
    if (!('vibrate' in navigator)) return;
    function buzz(ms) { try { navigator.vibrate(ms); } catch (e) {} }
    document.addEventListener('click', function (e) {
      if (!e.target || !e.target.closest) return;
      if (e.target.closest('.bn-bnav__item, [data-bn-search-open], [data-bn-cart-drawer-trigger]')) {
        buzz(8);
      } else if (e.target.closest('[data-product-atc], .product-form--atc-button')) {
        buzz(15);
      }
    }, true); // capture phase — fires even if a handler stops propagation
  })();

  /* ── 5) Drag-to-dismiss the cart bottom sheet ──────────────────── */
  (function cartSheetDrag() {
    var mq = window.matchMedia('(max-width: 768px)');
    var panel = null;
    var startY = 0;
    var dy = 0;
    var dragging = false;

    document.addEventListener('touchstart', function (e) {
      if (!mq.matches) return;
      if (!document.body.classList.contains('bn-cart-drawer-open')) return;
      if (!e.target.closest || !e.target.closest('.bn-cart-drawer__header')) return;
      panel = document.querySelector('[data-bn-cart-drawer-panel]');
      if (!panel) return;
      startY = e.touches[0].clientY;
      dy = 0;
      dragging = true;
      document.body.classList.add('bn-cart-drawer-dragging');
    }, { passive: true });

    document.addEventListener('touchmove', function (e) {
      if (!dragging || !panel) return;
      dy = e.touches[0].clientY - startY;
      if (dy <= 0) { dy = 0; panel.style.transform = ''; return; }
      e.preventDefault(); // we own this gesture now
      panel.style.transform = 'translateY(' + dy + 'px)';
    }, { passive: false });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      document.body.classList.remove('bn-cart-drawer-dragging');
      if (panel) {
        // Drop the inline transform synchronously so the panel animates from
        // where the finger left it — down to off-screen (close) or back up.
        if (dy > 110) {
          var closeBtn = document.querySelector('[data-bn-cart-drawer-close]');
          if (closeBtn) closeBtn.click();
        }
        panel.style.transform = '';
      }
      panel = null;
      dy = 0;
    }
    document.addEventListener('touchend', endDrag, { passive: true });
    document.addEventListener('touchcancel', endDrag, { passive: true });
  })();
})();
